import { useCallback, useRef } from 'react';

import { AnimatedCollapse } from '@backrs_ui/common-ui/src/atoms/AnimatedCollapse';
import { AppIcon, ICON_SIZES } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Box, SxProps, Theme, Typography, Tooltip } from '@mui/material';

import type { ItemVariant, SidebarMenuItem } from './sidebar.types';

type SidebarItemProps = Readonly<{
  sidebarMenuItem: SidebarMenuItem;
  expandedItem?: string;
  setExpandedItem: (title?: string, shouldOpenSidebar?: boolean) => void;
  isSidebarOpen: boolean;
  setIsSideBarOpen?: (isOpen: boolean) => void; // used for desktop mini-variant
}>;

type ScrollableSubItemsProps = Readonly<{
  variant: Extract<SidebarMenuItem['variant'], { type: 'EXPANDABLE' }>;
  expandedItem?: string;
  setExpandedItem: (title?: string, shouldOpenSidebar?: boolean) => void;
  isSidebarOpen: boolean;
  setIsSideBarOpen?: (isOpen: boolean) => void;
}>;

const ITEM_HEIGHT = 32;

const ScrollableSubItems = ({
  variant,
  expandedItem,
  setExpandedItem,
  isSidebarOpen,
  setIsSideBarOpen,
}: ScrollableSubItemsProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollable = variant.scrollable;

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;

    if (!container || !scrollable?.hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - ITEM_HEIGHT;

    if (isNearBottom) {
      scrollable.onLoadMore();
    }
  }, [scrollable]);

  const maxHeight = scrollable ? ITEM_HEIGHT * scrollable.maxVisibleItems : undefined;

  return (
    <Box ref={scrollContainerRef} onScroll={handleScroll} sx={getSubItemsContainerSx(maxHeight)}>
      {variant.subItems.map((subItem, index) => (
        <SidebarItem
          key={index}
          sidebarMenuItem={subItem}
          expandedItem={expandedItem}
          setExpandedItem={setExpandedItem}
          isSidebarOpen={isSidebarOpen}
          setIsSideBarOpen={setIsSideBarOpen}
        />
      ))}
    </Box>
  );
};

export const SidebarItem = ({
  sidebarMenuItem,
  expandedItem,
  setExpandedItem,
  isSidebarOpen,
  setIsSideBarOpen,
}: SidebarItemProps) => {
  const { id, title, leftIcon, variant, rightIcon } = sidebarMenuItem;

  if (sidebarMenuItem.hidden) return null;

  const isExpandable = variant.type === 'EXPANDABLE';
  const isExpanded = expandedItem === title;

  const hasActiveSubItem =
    isExpandable &&
    variant.subItems.some(
      (subItem) => subItem.variant.type === 'NAVIGATION_ITEM' && subItem.variant.isActive,
    );

  const isActive = (!isExpandable && variant.isActive) || hasActiveSubItem || false;
  const itemVariant = !isExpandable ? variant.itemVariant : undefined;
  const isSubItem = itemVariant !== undefined && itemVariant !== 'primary';
  const shouldShowTooltip = !isSidebarOpen && !isActive;

  const handleClick = () => {
    if (isExpandable) {
      if (variant.onClick) {
        variant.onClick();
      }

      if (variant.suppressExpandOnClick) return;

      setExpandedItem(isExpanded ? undefined : title, true);

      return;
    }

    if (!isSubItem) setExpandedItem(undefined);

    if (setIsSideBarOpen) setIsSideBarOpen(false); // collapse desktop mini-variant after click if desired

    variant.onClick();
  };

  return (
    <Box>
      <Tooltip title={shouldShowTooltip ? title : ''} placement="right" arrow enterDelay={100}>
        <Box
          id={id}
          sx={getContentContainerSx(isActive || isExpanded, itemVariant, isSidebarOpen)}
          onClick={handleClick}
        >
          <Box sx={leftIconTitleContainerSx}>
            <Box sx={iconContainerSx(leftIcon?.onlyShowOnActive, isActive)}>
              {leftIcon &&
                (leftIcon.component ? (
                  leftIcon.component
                ) : leftIcon.iconName ? (
                  <AppIcon
                    iconName={leftIcon.iconName}
                    diameter={leftIcon.diameter ?? ICON_SIZES['20px']}
                    color={
                      itemVariant === 'quaternary' ? 'Gray/600' : 'Colors/Text/text-quaternary'
                    }
                    rotate={leftIcon.rotate}
                  />
                ) : null)}
            </Box>
            {itemVariant === 'quaternary' ? (
              <Typography sx={getTitleTextSx(isActive, itemVariant)}>{title}</Typography>
            ) : (
              <AnimatedCollapse isOpen={isSidebarOpen} direction="horizontal">
                <Typography sx={getTitleTextSx(isActive, itemVariant)}>{title}</Typography>
              </AnimatedCollapse>
            )}
          </Box>

          {isExpandable && (
            <AnimatedCollapse isOpen={isSidebarOpen} opacityFade>
              <Box sx={chevronContainerSx(isExpanded)}>
                <AppIcon
                  iconName="ChevronDown"
                  diameter="20px"
                  color="Colors/Text/text-quaternary"
                />
              </Box>
            </AnimatedCollapse>
          )}

          {rightIcon && isSidebarOpen && (
            <Box sx={rightIconContainerSx}>
              <AppIcon
                iconName={rightIcon.iconName}
                diameter="16px"
                color="Colors/Foreground/fg-quinary"
              />
            </Box>
          )}
        </Box>
      </Tooltip>

      {isExpandable && isSidebarOpen && (
        <AnimatedCollapse isOpen={isExpanded}>
          <ScrollableSubItems
            variant={variant}
            expandedItem={expandedItem}
            setExpandedItem={setExpandedItem}
            isSidebarOpen={isSidebarOpen}
            setIsSideBarOpen={setIsSideBarOpen}
          />
        </AnimatedCollapse>
      )}
    </Box>
  );
};

const getContentContainerSx = (
  isActive: boolean,
  itemVariant: ItemVariant | undefined,
  isSideBarOpen: boolean,
): SxProps<Theme> => {
  if (itemVariant === 'quaternary') return contentContainerSxQuaternary;

  const isSubItem = itemVariant !== undefined && itemVariant !== 'primary';

  return contentContainerSx(isActive, isSubItem, isSideBarOpen);
};

const contentContainerSx =
  (isActive: boolean, isSubItem: boolean, isSideBarOpen: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    borderRadius: backrs.radius['radius-sm'],
    px: backrs.spacing['spacing-lg'],
    py: backrs.spacing['spacing-md'],
    border: '1px solid transparent',
    minWidth: backrs.spacing['spacing-5xl'],
    minHeight: backrs.spacing['spacing-5xl'],
    transition: 'all 0.3s ease',
    maxWidth: { xs: '100%', sm: !isSideBarOpen ? '48px' : '100%' },
    ...(isActive
      ? {
          backgroundColor: backrs.colors['Colors/Background/bg-primary'],
          color: backrs.colors['Colors/Text/text-brand-primary-hover'],
          border: `1px solid ${backrs.colors['Colors/Text/text-primary']}`,
        }
      : {}),
    '&:hover': {
      backgroundColor: backrs.colors['Colors/Background/bg-primary'],
      transform: isSubItem ? undefined : 'translate(-2px, -2px)',
      boxShadow: isSubItem ? 'none' : '4px 4px 0px 0px rgba(0, 0, 0, 1)',
      color: backrs.colors['Colors/Text/text-brand-primary-hover'],
      border: `1px solid ${backrs.colors['Colors/Text/text-primary']}`,
    },
    '&:active': {
      transform: 'translate(0px, 0px)',
      boxShadow: 'none',
      transition: 'all 0s',
    },
  });

const contentContainerSxQuaternary: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  borderRadius: backrs.radius['radius-sm'],
  px: backrs.spacing['spacing-md'],
  py: backrs.spacing['spacing-xs'],
  pl: backrs.spacing['spacing-lg'],
  transition: 'all 0.3s ease',
  width: '100%',
  '&:hover': {
    color: backrs.colors['Colors/Text/text-brand-primary-hover'],
  },
});

const leftIconTitleContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});

const getTitleTextSx = (isActive: boolean, itemVariant?: ItemVariant): SxProps<Theme> => {
  if (itemVariant === 'quaternary') return titleTextSxQuaternary;
  if (itemVariant === 'tertiary') return titleTextSxTertiary;
  if (itemVariant === 'secondary') return titleTextSxSecondary(isActive);

  return titleTextSxPrimary(isActive);
};

const titleTextSxPrimary =
  (isActive: boolean): SxProps<Theme> =>
  ({ backrs, typography }) => ({
    userSelect: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
      color: backrs.colors['Colors/Text/text-brand-primary-hover'],
    },
    color: isActive
      ? backrs.colors['Colors/Text/text-primary']
      : backrs.colors['Colors/Text/text-secondary'],
    ...typography['text-md-semibold'],
  });

const titleTextSxSecondary =
  (isActive: boolean): SxProps<Theme> =>
  ({ backrs, typography }) => ({
    userSelect: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
      color: backrs.colors['Colors/Text/text-brand-primary-hover'],
    },
    color: isActive
      ? backrs.colors['Colors/Text/text-primary']
      : backrs.colors['Colors/Text/text-secondary'],
    ...typography['text-md-semibold'],
  });

const titleTextSxTertiary: SxProps<Theme> = ({ backrs, typography }) => ({
  userSelect: 'none',
  whiteSpace: 'nowrap',
  '&:hover': {
    color: backrs.colors['Colors/Text/text-brand-primary-hover'],
  },
  color: backrs.colors['Colors/Text/text-quaternary'],
  ...typography['text-sm-medium'],
});

const titleTextSxQuaternary: SxProps<Theme> = ({ backrs, typography }) => ({
  userSelect: 'none',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  flex: 1,
  minWidth: 0,
  '&:hover': {
    color: backrs.colors['Gray/700'],
  },
  color: backrs.colors['Gray/600'],
  pl: backrs.spacing['spacing-xxs'],
  ...typography['text-sm-regular'],
});

const chevronContainerSx = (isExpanded: boolean): SxProps<Theme> => ({
  transition: `transform ${isExpanded ? '0.2s ease-in-out' : '0.2s ease'}`,
  transform: isExpanded ? 'rotate(-180deg)' : 'rotate(0deg)',
  height: ICON_SIZES['20px'],
  display: 'flex',
  alignItems: 'center',
});

const rightIconContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
};

const iconContainerSx =
  (onlyShowOnActive?: boolean, isActive?: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    py: backrs.spacing['spacing-xs'],
    boxSizing: 'border-box',
    width: ICON_SIZES['20px'],
    minWidth: ICON_SIZES['20px'],
    height: ICON_SIZES['20px'],
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...(onlyShowOnActive
      ? {
          opacity: isActive ? 1 : 0,
          transition: 'opacity 0.2s ease',
        }
      : {}),
  });

const getSubItemsContainerSx =
  (maxHeight?: number): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    mt: backrs.spacing['spacing-sm'],
    pb: backrs.spacing['spacing-md'],
    ...(maxHeight !== undefined
      ? {
          maxHeight: `${maxHeight}px`,
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: backrs.colors['Colors/Border/border-secondary'],
            borderRadius: '2px',
          },
        }
      : {}),
  });
